// Blog content for CompareYourOptions.com.au
// Pure data only (no imports) so it can also be parsed by the sitemap generator.
// To add a new article: append an object below (or use the AI draft tool in /admin
// to generate one and paste the result here), then redeploy.

export const BLOG_POSTS = [
  {
    slug: 'is-solar-worth-it-in-victoria',
    title: 'Is Solar Worth It in Victoria in 2026?',
    seoTitle: 'Is Solar Worth It in Victoria in 2026? | CompareYourOptions',
    metaDescription:
      'Wondering if solar panels are worth it in Victoria in 2026? We break down payback periods, typical savings, rebates and how to know if your home is a good fit.',
    excerpt:
      'Rising power prices, generous rebates and cheaper panels make 2026 one of the best years yet to go solar in Victoria. Here is how to tell if it stacks up for your home.',
    category: 'Solar',
    tags: ['solar', 'victoria', 'savings', 'rebates'],
    author: 'CompareYourOptions Team',
    datePublished: '2026-01-12',
    dateModified: '2026-02-02',
    readMinutes: 5,
    heroImage: '/blog/is-solar-worth-it-in-victoria.jpg',
    body: `## Short answer: for most Victorian homes, yes

With electricity prices climbing and panel costs near record lows, solar continues to be one of the best-value upgrades a Victorian household can make. Many homes now see payback in **3 to 5 years**, then enjoy a decade or more of much lower bills.

But "worth it" always depends on your home. Here is what actually moves the needle.

## What affects your solar payback

- **How much power you use — and when.** The more energy you can use during daylight (or store in a battery), the faster solar pays off.
- **Roof orientation and shading.** North, east and west-facing roofs all work well. Heavy shading from trees or neighbouring buildings reduces output.
- **System size and quality.** A well-sized system matched to your usage beats an oversized bargain system.
- **Rebates and incentives.** Eligible Victorian households can access Solar Homes Program rebates plus federal incentives, cutting upfront costs significantly.

## Typical savings in Victoria

A quality **6.6kW** system is a popular fit for many homes and can offset a large share of daytime usage. Households commonly report bill reductions of **50–70%** once their system is sized correctly — and adding a battery can push that higher by covering expensive evening peak hours.

> These figures are indicative. Your actual savings depend on usage, tariff, roof and system.

## Solar with or without a battery?

If your goal is the fastest payback, solar alone is usually the place to start. If you use a lot of power in the evening, want backup during outages, or want to maximise independence from the grid, a battery is worth comparing at the same time — many systems can be installed battery-ready.

## How to know what is right for your home

Every roof, household and budget is different. The quickest way to see real numbers for *your* address is to compare your options — it takes about two minutes and there is no obligation.`,
    faqs: [
      {
        q: 'How long does solar take to pay for itself in Victoria?',
        a: 'For many Victorian homes a well-sized system pays for itself in around 3 to 5 years, after which the savings continue for the life of the system. Payback depends on your usage, tariff, roof and any rebates you qualify for.',
      },
      {
        q: 'Do I need a battery to make solar worthwhile?',
        a: 'No. Solar alone often delivers the fastest payback. A battery adds value if you use a lot of power in the evening, want backup, or want to maximise self-consumption — and you can compare both at once.',
      },
      {
        q: 'Are there rebates for solar in Victoria?',
        a: 'Eligible households can access Solar Victoria’s Solar Homes Program rebate plus federal incentives. Eligibility criteria and amounts apply and can change, so it is worth checking your current eligibility.',
      },
    ],
  },
  {
    slug: 'solar-vs-battery-what-to-add-first',
    title: 'Solar vs Battery: What Should You Add First?',
    seoTitle: 'Solar vs Battery: What to Add First? | CompareYourOptions',
    metaDescription:
      'Solar first or battery first? Learn which upgrade delivers faster savings, when a battery makes sense, and how to plan a battery-ready solar system.',
    excerpt:
      'If you can only do one upgrade now, which wins — solar or a battery? Here is a simple way to decide based on how (and when) your household uses power.',
    category: 'Battery',
    tags: ['solar', 'battery', 'planning'],
    author: 'CompareYourOptions Team',
    datePublished: '2026-01-20',
    dateModified: '2026-02-02',
    readMinutes: 4,
    heroImage: '/blog/solar-vs-battery-what-to-add-first.jpg',
    body: `## The general rule: solar first, then battery

For most homes **without** solar, panels are the better first step. Solar reduces the power you buy from the grid during the day immediately, and typically pays back faster than a battery on its own.

A battery shines once you already generate solar and want to use more of it after the sun goes down.

## When a battery makes sense sooner

Consider a battery earlier (or at the same time as solar) if you:

- Use a lot of power in the **evening** when solar isn't generating
- Experience regular **blackouts** and want backup
- Want to **maximise independence** from the grid
- Are on a tariff with expensive peak evening rates

## Plan for a battery-ready system

Even if you start with solar only, ask for a **battery-ready** design. This makes adding storage later simpler and cheaper, and avoids reworking your system down the track.

## A quick way to decide

| Your situation | Best first step |
| --- | --- |
| No solar yet | Solar (battery-ready) |
| Have solar, high evening use | Battery |
| Frequent outages | Solar + battery |

> Indicative guidance only — the right mix depends on your home and usage.

## Compare both at once

You don't have to guess. Compare solar and battery options for your address together and see indicative pricing and savings side by side.`,
    faqs: [
      {
        q: 'Should I get solar or a battery first?',
        a: 'If you do not yet have solar, panels are usually the better first step because they cut your daytime grid usage immediately and pay back faster. Add a battery once you want to use more of your solar in the evenings.',
      },
      {
        q: 'What does "battery-ready" mean?',
        a: 'A battery-ready solar system is designed so a battery can be added later with minimal extra work — saving you money and hassle when you decide to add storage.',
      },
    ],
  },
  {
    slug: 'solar-homes-rebate-changes-explained',
    title: 'Solar Homes Rebate Changes Explained (2026)',
    seoTitle: 'Solar Homes Rebate Changes 2026 Explained | CompareYourOptions',
    metaDescription:
      'Victoria’s Solar Homes Program income cap is changing from 1 July 2026. Learn what is changing, who is affected and how to check your eligibility before the deadline.',
    excerpt:
      'From 1 July 2026 the household income cap for solar and hot water rebates is tightening. Here is what is changing and why some households should act sooner.',
    category: 'Rebates',
    tags: ['rebates', 'solar victoria', 'eligibility', 'deadline'],
    author: 'CompareYourOptions Team',
    datePublished: '2026-01-28',
    dateModified: '2026-02-03',
    readMinutes: 4,
    heroImage: '/blog/solar-homes-rebate-changes-explained.jpg',
    body: `## What is changing

From **1 July 2026**, the combined household income cap to qualify for Solar Victoria's Solar Homes Program rebates for **solar PV and hot water** is reducing from **$210,000** to **$150,000** per year.

To secure a rebate under the current $210,000 cap, eligible applications generally need to be **submitted in full by 5pm, Tuesday 30 June 2026**.

## Who is most affected

Households with a combined income **between $150,000 and $210,000** are most likely to miss out after the change. If that's you, it's worth acting before the deadline rather than risking missing out.

Solar Victoria has noted most existing customers will still qualify under the new cap — but the in-between band is the group with the most to lose.

## What rebates are available right now

- **Solar PV:** rebate for eligible homes, plus an optional interest-free loan
- **Hot water (heat pump):** rebate for eligible systems

> Rebate amounts, eligibility and availability are set by Solar Victoria and may change. Always confirm your current eligibility.

## Don't leave it to the last week

Applications take time to prepare and submit correctly. Starting early gives you the best chance of locking in a rebate before the rules change.

## Check your eligibility

Not sure if you're affected? Comparing your options is free and takes a couple of minutes — and we'll help you understand what you may qualify for.`,
    faqs: [
      {
        q: 'When do the Solar Homes rebate changes take effect?',
        a: 'The lower household income cap of $150,000 applies from 1 July 2026. To qualify under the current $210,000 cap, eligible applications generally need to be submitted in full by 5pm on Tuesday 30 June 2026.',
      },
      {
        q: 'Who is affected by the rebate income cap change?',
        a: 'Households with a combined income between $150,000 and $210,000 are most affected, as they may no longer be eligible after 1 July 2026. Households under $150,000 generally remain eligible.',
      },
    ],
  },
  {
    slug: 'heat-pump-hot-water-explained',
    title: 'Heat Pump Hot Water Explained: Save Up to 4x on Hot Water',
    seoTitle: 'Heat Pump Hot Water Explained | CompareYourOptions',
    metaDescription:
      'Heat pump hot water systems can use up to 4x less energy than a conventional electric system. Learn how they work, what they cost and whether one suits your home.',
    excerpt:
      'Hot water is one of the biggest energy users in the home. A heat pump system can slash that cost — here is how the technology works and why it pays off.',
    category: 'Hot Water',
    tags: ['heat pump', 'hot water', 'efficiency', 'rebates'],
    author: 'CompareYourOptions Team',
    datePublished: '2026-02-01',
    dateModified: '2026-02-03',
    readMinutes: 5,
    heroImage: '/blog/heat-pump-hot-water-explained.jpg',
    body: `## Hot water is a hidden energy cost

For many Australian homes, water heating is one of the **largest** slices of the energy bill. Switching to a more efficient system is one of the simplest ways to cut that cost — and a heat pump is among the most efficient options available.

## How a heat pump works

Rather than using electricity to heat water directly (like a conventional electric element), a heat pump **moves heat** from the surrounding air into the water tank. Because it transfers heat instead of generating it, it can use up to **four times less energy** for the same amount of hot water.

Think of it like a reverse refrigerator — it's a well-proven, efficient technology.

## Why it pays off

- **Lower running costs** — typically a fraction of a conventional electric system
- **Pairs brilliantly with solar** — run it during the day on your own solar energy
- **Rebate-eligible** — eligible Victorian households can access hot water rebates
- **Lower emissions** — less energy used means a smaller footprint

## Is it right for your home?

Heat pumps suit most homes, but the ideal size and model depend on your household size, hot water habits and where the unit will be installed. A quick comparison gives you a tailored recommendation.

## Compare heat pump options

See indicative pricing and rebate-friendly options for your home — free, and with no obligation.`,
    faqs: [
      {
        q: 'How much can a heat pump hot water system save?',
        a: 'Because a heat pump moves heat rather than generating it, it can use up to four times less energy than a conventional electric system — translating into significantly lower running costs, especially when paired with solar.',
      },
      {
        q: 'Are heat pump hot water systems eligible for rebates?',
        a: 'Yes — eligible Victorian households can access hot water rebates under Solar Victoria’s Solar Homes Program. Eligibility and amounts apply and may change, so check your current eligibility.',
      },
    ],
  },
  {
    slug: 'how-much-can-a-home-battery-save-you',
    title: 'How Much Can a Home Battery Save You? (Australian Guide)',
    seoTitle: 'How Much Can a Home Battery Save You? | CompareYourOptions',
    metaDescription:
      'How much does a home battery actually save? Learn what drives battery savings, typical payback, backup benefits and how to size a battery for your home.',
    excerpt:
      'A home battery lets you use your solar after dark and ride through outages — but how much does it really save? Here is what drives the numbers.',
    category: 'Battery',
    tags: ['battery', 'savings', 'backup', 'solar'],
    author: 'CompareYourOptions Team',
    datePublished: '2026-02-03',
    dateModified: '2026-02-03',
    readMinutes: 4,
    heroImage: '/blog/how-much-can-a-home-battery-save-you.jpg',
    body: `## What a battery actually does

A home battery stores the solar energy your panels generate during the day so you can use it at night — instead of buying expensive grid power during the evening peak. It can also keep essential circuits running during a blackout.

## What drives your battery savings

- **Evening usage.** The more power you use after dark, the more a battery saves.
- **Solar size.** You need enough surplus solar to charge the battery each day.
- **Tariff.** Big gaps between peak and off-peak rates make a battery more valuable.
- **Battery size.** A 10kWh battery suits many homes; larger homes may benefit from 13kWh or more.

## Typical savings and payback

Many households use a battery to avoid a large share of their evening grid usage. Combined with solar, this can lift total bill savings well beyond what solar achieves alone. Payback varies more than solar — it depends heavily on your usage pattern and tariff.

> Indicative only. Your savings depend on usage, tariff and system size.

## Don't forget backup

Beyond savings, many homeowners value the **peace of mind** of keeping the fridge, lights and internet running during an outage. Backup capability depends on your system setup.

## See your numbers

The best way to understand battery value for your home is to compare options for your address and usage — it's free and takes a couple of minutes.`,
    faqs: [
      {
        q: 'How much can a home battery save me?',
        a: 'Savings depend on how much power you use in the evening, your solar size and your tariff. A battery lets you use stored solar at night instead of buying peak grid power, which can lift your total savings well beyond solar alone.',
      },
      {
        q: 'What size home battery do I need?',
        a: 'A 10kWh battery suits many households, while larger homes or those with high evening usage may benefit from 13kWh or more. The right size depends on your usage and solar system — a quick comparison gives a tailored recommendation.',
      },
    ],
  },
];

export const getPost = (slug) => BLOG_POSTS.find((p) => p.slug === slug);

export const getRelatedPosts = (post, limit = 3) =>
  BLOG_POSTS.filter((p) => p.slug !== post.slug && p.category === post.category)
    .concat(BLOG_POSTS.filter((p) => p.slug !== post.slug && p.category !== post.category))
    .slice(0, limit);
